import { ActionIcon, SearchIcon } from "~/assets/icons";
import styles from "./WorkOrderList.module.scss";
import classNames from "classnames/bind";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { ColumnType } from "~/utils/types/common";
import dayjs from "dayjs";
import { BaseTable } from "~/components/common";
import { useState, useRef, useEffect, useMemo } from "react";
import { handleGetOverviewWorkOrderList } from "~/thunks/job-details/jobDetailsThunk";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { notify } from "~/utils/helper";
import ModalAlertDelete from "~/components/common/modal-alert-delete/ModalAlertDelete";
import jobDetailsApi from "~/apis/jobDetails";

const cx = classNames.bind(styles);

interface WorkOrderListProps {
  jobId: string;
  onCreateWorkOrder?: () => void;
  onViewWorkOrder?: (workOrderId: string) => void;
  onEditWorkOrder?: (workOrderId: string) => void;
  onDeleteWorkOrder?: (workOrderId: string) => void;
  onCreateSuccess?: () => void;
}

const WorkOrderList = ({
  jobId,
  onCreateWorkOrder,
  onViewWorkOrder,
  onEditWorkOrder,
  onDeleteWorkOrder,
  onCreateSuccess,
}: WorkOrderListProps) => {
  const dispatch = useAppDispatch();
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const { overviewWorkOrderList } = useAppSelector((state) => state.jobDetails);
  const [isOpendModalDelete, setIsOpendModalDelete] = useState(false);
  const [workOrderIdToDelete, setWorkOrderIdToDelete] = useState<string | null>(
    null
  );

  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Status filter options
  const statusOptions = [
    { value: "", label: "All Orders" },
    { value: "Completed", label: "Completed" },
    { value: "Pending", label: "Pending" },
    { value: "In Progress", label: "In Progress" },
  ];

  useEffect(() => {
    if (jobId) {
      dispatch(handleGetOverviewWorkOrderList({ jobId: parseInt(jobId) }))
        .unwrap()
        .catch((error) => {
          notify("error", `Failed to fetch work order list: ${error}`);
        });
    }
  }, [dispatch, jobId, onCreateSuccess]);

  // Filter and search logic
  const filteredWorkOrders = useMemo(() => {
    let filtered = overviewWorkOrderList?.responses || [];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((workOrder: any) => {
        const workOrderId = workOrder.workOrderId?.toLowerCase() || "";
        const type = workOrder.type?.toLowerCase() || "";
        const status = workOrder.status?.toLowerCase() || "";
        const subcontractors =
          workOrder.subcontractors
            ?.map((sub: any) => sub.account?.username?.toLowerCase())
            .join(" ") || "";
        const salesRep = workOrder.job?.salesRep?.username?.toLowerCase() || "";

        return (
          workOrderId.includes(query) ||
          type.includes(query) ||
          status.includes(query) ||
          subcontractors.includes(query) ||
          salesRep.includes(query)
        );
      });
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(
        (workOrder: any) => workOrder.status === statusFilter
      );
    }

    return filtered;
  }, [overviewWorkOrderList?.responses, searchQuery, statusFilter]);

  // Action Menu Component
  const ActionMenu = ({ workOrder, onClose, position }: any) => {
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const handleView = () => {
      if (
        onViewWorkOrder &&
        workOrder?.workOrderId !== undefined &&
        workOrder?.workOrderId !== null
      ) {
        onViewWorkOrder(workOrder.id.toString());
        console.log("View work order:", workOrder.workOrderId);
      }
      onClose && onClose();
    };

    const handleEdit = () => {
      if (
        onEditWorkOrder &&
        workOrder?.workOrderId !== undefined &&
        workOrder?.workOrderId !== null
      ) {
        onEditWorkOrder(workOrder.id.toString());
      }
      onClose();
    };

    const handleDelete = () => {
      if (
        onDeleteWorkOrder &&
        workOrder?.workOrderId !== undefined &&
        workOrder?.workOrderId !== null
      ) {
        setWorkOrderIdToDelete(workOrder.id.toString());
        setIsOpendModalDelete(true);
      }
      onClose();
    };

    return (
      <div
        ref={menuRef}
        className={cx("action-menu")}
        style={{
          top: position.top,
          left: position.left,
        }}
      >
        <button className={cx("menu-item")} onClick={handleView}>
          View
        </button>
        <button className={cx("menu-item")} onClick={handleEdit}>
          Edit
        </button>
        <button className={cx("menu-item")} onClick={handleDelete}>
          Delete
        </button>
      </div>
    );
  };

  const handleActionClick = (event: React.MouseEvent, workOrder: any) => {
    event.stopPropagation();

    if (activeMenu === workOrder.id) {
      setActiveMenu(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 5,
      left: rect.left - 100,
    });
    setActiveMenu(workOrder.id);
  };

  const columns: Array<ColumnType> = [
    {
      id: "workOrderId",
      labelHeader: "Work Order ID",
      width: "13%",
      renderCell: (workOrder: any, index: number) => (
        <p className={cx("work-order-id")}>{workOrder.workOrderId || ""}</p>
      ),
    },
    {
      id: "dueDate",
      labelHeader: "Due Date",
      width: "13%",
      renderCell: (workOrder: any, index: number) => (
        <div className={cx("due-date-container")}>
          <p className={cx("due-date")}>
            {workOrder?.dueDate
              ? dayjs(workOrder?.dueDate).format("YYYY-MM-DD")
              : "No due date yet."}
          </p>
          <p className={cx("time")}>
            {workOrder?.dueDate
              ? dayjs(workOrder?.dueDate).format("HH:mm")
              : "No due date yet."}
          </p>
        </div>
      ),
    },
    {
      id: "subcontractor",
      labelHeader: "Subcontractors",
      width: "13%",
      renderCell: (workOrder: any, index: number) => (
        <div className={cx("subcontractor-container")}>
          <p className={cx("subcontractor-item")}>
            {workOrder.subcontractors
              ?.map((subcontractor: any) => subcontractor?.account?.username)
              .join(", ")}
          </p>
        </div>
      ),
    },
    {
      id: "saleReprsentative",
      labelHeader: "Sales Representative",
      width: "13%",
      renderCell: (workOrder: any, index: number) => (
        <p className={cx("sales-representative-name")}>
          {workOrder.job?.salesRep?.username || ""}
        </p>
      ),
    },
    {
      id: "type",
      labelHeader: "Type",
      width: "13%",
      renderCell: (workOrder: any, index: number) => (
        <div className={cx("type-cell", `${workOrder.type.toLowerCase()}`)}>
          <p className={cx("type")}>{workOrder.type || ""}</p>
        </div>
      ),
    },
    {
      id: "total",
      labelHeader: "Total",
      width: "13%",
      renderCell: (workOrder: any, index: number) => (
        <p className={cx("total")}>$ {workOrder.totalPrice || ""}</p>
      ),
    },
    {
      id: "status",
      labelHeader: "Status",
      width: "13%",
      renderCell: (workOrder: any, index: number) => (
        <div
          className={cx(
            "status-cell",
            workOrder.status === "In Progress"
              ? "in-progress"
              : workOrder.status === "Pending"
              ? "pending"
              : "completed"
          )}
        >
          <p className={cx("status")}>{workOrder.status || ""}</p>
        </div>
      ),
    },
    {
      id: "actions",
      labelHeader: "",
      width: "9%",
      renderCell: (workOrder: any, index: number) => (
        <div className={cx("action-icon-container")}>
          <div onClick={(e) => handleActionClick(e, workOrder)}>
            <ActionIcon className={cx("action-icon")} />
          </div>
          {activeMenu === workOrder.id && (
            <ActionMenu
              workOrder={workOrder}
              onClose={() => setActiveMenu(null)}
              position={menuPosition}
            />
          )}
        </div>
      ),
    },
  ];

  const onDeleteWorkOrderConfirm = async () => {
    if (workOrderIdToDelete) {
      try {
        await jobDetailsApi.deleteWorkOrder(workOrderIdToDelete);
        notify("success", "Work order deleted successfully");
        setIsOpendModalDelete(false);
        setWorkOrderIdToDelete(null);
        dispatch(handleGetOverviewWorkOrderList({ jobId: parseInt(jobId) }))
          .unwrap()
          .catch((error) => {
            notify("error", `Failed to fetch work order list: ${error}`);
          });
      } catch (error: any) {
        notify(
          "error",
          `Failed to delete work order: ${
            error?.response?.data?.message || "Unknown error"
          }`
        );
      }
    } else {
      notify("error", "Work order not found");
    }
  };

  return (
    <div className={cx("work-order-list-container")}>
      <div className={cx("header")}>
        <div className={cx("item")}>
          <div className={cx("search-container")}>
            <SearchIcon className={cx("search-icon")} />
            <input
              type="text"
              placeholder="Search"
              className={cx("search-input")}
              maxLength={50}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <PrimarySelectInput
            name="status"
            placeholder="All Orders"
            value={statusFilter}
            onChange={(value: string) => setStatusFilter(value)}
            options={statusOptions}
            className={cx("status-select")}
            classNameIcon={cx("status-select-icon")}
          />
        </div>

        <div className={cx("item")}>
          <PrimaryButton
            title="Add Work Order"
            onClick={onCreateWorkOrder}
            className={cx("create-button")}
          />
        </div>
      </div>

      <div className={cx("body")}>
        <BaseTable
          data={filteredWorkOrders}
          columns={columns}
          page={1}
          totalPages={1}
          className={cx("table")}
        />
      </div>

      <ModalAlertDelete
        open={isOpendModalDelete}
        description={"Are you sure you want to delete this work order?"}
        onCancel={() => {
          setIsOpendModalDelete(false);
        }}
        onConfirm={onDeleteWorkOrderConfirm}
      />
    </div>
  );
};

export default WorkOrderList;
